import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, Volume2, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Kid-facing "Picture Dictionary".
 *
 * Turns the seeded 62-concept illustration corpus into a browsable, audio-first
 * visual glossary. A child taps any card → hears the concept read aloud.
 */
export default function PictureDictionaryPage() {
  const { childId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null);

  const backendBase = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Public admin list — kids get a curated view. The endpoint requires admin,
        // so we fall back to a lightweight public list if the child isn't admin.
        const res = await api.get('/picture-dictionary').catch(() => null);
        if (cancelled) return;
        if (res && res.data && Array.isArray(res.data.items)) {
          setItems(res.data.items);
        } else {
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const speak = useCallback((text) => {
    try {
      if (!('speechSynthesis' in window)) return;
      window.speechSynthesis.cancel();
      const utter = new window.SpeechSynthesisUtterance(text);
      utter.rate = 0.95;
      utter.pitch = 1.05;
      window.speechSynthesis.speak(utter);
    } catch (err) { void err; }
  }, []);

  const subjects = useMemo(() => {
    const set = new Set(items.map((it) => it.subject).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [items]);

  const [broken, setBroken] = useState(() => new Set());
  const markBroken = (key) => setBroken((prev) => new Set(prev).add(key));

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((it) => {
      if (broken.has(it.cache_key || it.concept)) return false;
      if (filter !== 'all' && it.subject !== filter) return false;
      if (!needle) return true;
      return (
        (it.concept || '').toLowerCase().includes(needle) ||
        (it.subject || '').toLowerCase().includes(needle)
      );
    });
  }, [items, q, filter, broken]);

  const resolveImg = (url) => (url && url.startsWith('http')) ? url : `${backendBase}${url || ''}`;

  const openDetail = (item) => {
    setDetail(item);
    speak(`${item.concept}. ${item.explanation || ''}`.slice(0, 300));
  };
  const closeDetail = () => {
    try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (err) { void err; }
    setDetail(null);
  };

  const backLink = childId ? `/learn/${childId}` : '/dashboard';

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-emerald-50">
      <header className="bg-white/80 backdrop-blur-md border-b border-sky-100 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(backLink)} data-testid="pic-dict-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-fredoka font-bold text-lg sm:text-xl text-slate-800">Picture Dictionary</h1>
              <p className="text-xs text-slate-500">Tap any picture to hear it &amp; learn</p>
            </div>
          </div>

          <div className="flex gap-2 flex-1 sm:max-w-md">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="pl-9 rounded-full"
                data-testid="pic-dict-search"
              />
            </div>
          </div>
        </div>

        {subjects.length > 1 && (
          <div className="max-w-5xl mx-auto px-4 pb-3 -mt-1 flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {subjects.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilter(s)}
                className={`shrink-0 rounded-full px-4 py-1 text-sm capitalize transition ${
                  filter === s ? 'bg-emerald-500 text-white shadow' : 'bg-white text-slate-600 border border-slate-200 hover:border-emerald-300'
                }`}
                data-testid={`pic-dict-filter-${s}`}
              >
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>
        )}
      </header>

      <main className="max-w-5xl mx-auto p-4 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-slate-400 py-16">
            No pictures{q ? ' match your search' : ' yet — check back soon!'}.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="pic-dict-grid">
            {filtered.map((item, index) => (
              <motion.button
                key={item.cache_key || item.concept}
                type="button"
                onClick={() => openDetail(item)}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.02, 0.5) }}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className="text-left"
                data-testid={`pic-dict-card-${item.cache_key || item.concept}`}
              >
                <Card className="overflow-hidden border-0 shadow-md hover:shadow-lg transition-shadow">
                  <div className="aspect-square bg-gradient-to-br from-slate-50 to-white">
                    <img
                      src={resolveImg(item.image_url)}
                      alt={item.concept}
                      loading="lazy"
                      onError={() => markBroken(item.cache_key || item.concept)}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <CardContent className="p-3">
                    <p className="text-sm font-fredoka font-bold text-slate-800 leading-tight truncate">{item.concept}</p>
                    <div className="flex items-center justify-between mt-1">
                      <Badge variant="outline" className="text-[10px] capitalize">{item.subject}</Badge>
                      <Volume2 className="w-4 h-4 text-emerald-500" />
                    </div>
                  </CardContent>
                </Card>
              </motion.button>
            ))}
          </div>
        )}
      </main>

      {/* Detail overlay */}
      {detail && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeDetail}
          data-testid="pic-dict-detail"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            className="bg-white rounded-3xl overflow-hidden max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img src={resolveImg(detail.image_url)} alt={detail.concept} className="w-full h-64 object-contain bg-gradient-to-br from-sky-50 to-emerald-50" />
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-3 right-3 rounded-full bg-white/90 hover:bg-white shadow"
                onClick={closeDetail}
                aria-label="Close"
                data-testid="pic-dict-close"
              >
                <X className="w-5 h-5 text-slate-700" />
              </Button>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-fredoka font-bold text-slate-800">{detail.concept}</h2>
                <Badge variant="outline" className="capitalize">{detail.subject}</Badge>
              </div>
              {detail.explanation && (
                <p className="text-slate-600 leading-relaxed">{detail.explanation}</p>
              )}
              <Button
                onClick={() => speak(`${detail.concept}. ${detail.explanation || ''}`.slice(0, 300))}
                className="mt-4 w-full rounded-full bg-emerald-500 hover:bg-emerald-600"
                data-testid="pic-dict-speak"
              >
                <Volume2 className="w-4 h-4 mr-2" /> Read it to me
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
